import { useEffect, useCallback, useRef } from 'react';
import { dashboardService } from '@/services/dashboardService';
import { useDashboardStore } from '@/stores/dashboardStore';
import type { DashboardFilters, ChatAction } from '@/types/dashboard';

const DEFAULT_LIMIT = 500;

export function useDashboard() {
  const {
    currentTab,
    filters,
    tabData,
    loading,
    error,
    globalPeriod,
    filterInfo,
    setCurrentTab,
    setFilters,
    setTabData,
    appendBlockRows,
    replaceBlockRows,
    setLoading,
    setError,
    setMeta,
    openModal,
    closeModal,
  } = useDashboardStore();

  // 每个明细块的已加载 offset（key: `${tabId}|${block}`）；-1 表示该块已全部加载
  const blockOffsetsRef = useRef<Record<string, number>>({});
  // 每个明细块当前生效的后端排序（key: `${tabId}|${block}`）
  const blockSortsRef = useRef<Record<string, { sortCol: string; sortOrder: 'asc' | 'desc' }>>({});
  // 防止同一明细块并发触发 loadMore
  const loadingMoreRef = useRef<Record<string, boolean>>({});
  // switchTab 已主动加载的 tab，effect 不再重复请求
  const pendingSwitchTabRef = useRef<string | null>(null);
  // 当前 tab 数据请求的 AbortController，切换 tab 时取消旧请求
  const abortControllerRef = useRef<AbortController | null>(null);

  /** tab 重新加载（首次加载 / 筛选变化）时清空该 tab 所有块的加载进度与排序 */
  const resetBlockOffsets = useCallback((tabId: string) => {
    const prefix = `${tabId}|`;
    for (const key of Object.keys(blockOffsetsRef.current)) {
      if (key.startsWith(prefix)) delete blockOffsetsRef.current[key];
    }
    for (const key of Object.keys(blockSortsRef.current)) {
      if (key.startsWith(prefix)) delete blockSortsRef.current[key];
    }
  }, []);

  /** 取消当前未完成的 tab 数据请求 */
  const cancelLoad = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /** 统一错误文案：DB 繁忙给可刷新提示，主动取消忽略 */
  const formatLoadError = useCallback((err: unknown): string | null => {
    if (err instanceof DOMException && err.name === 'AbortError') return null;
    const message = err instanceof Error ? err.message : String(err);
    if (message.includes('[DB_BUSY]')) return '数据加载繁忙，请刷新重试';
    return message || '加载失败';
  }, []);

  const loadTab = useCallback(
    async (tabId: string, activeFilters?: DashboardFilters) => {
      cancelLoad();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const data = await dashboardService.fetchTab(tabId, activeFilters ?? filters, undefined, controller.signal);
        if (!controller.signal.aborted) {
          setTabData(tabId, data);
          resetBlockOffsets(tabId);
        }
      } catch (err) {
        const message = formatLoadError(err);
        if (message) setError(message);
      } finally {
        // 只有当前 controller 仍是活跃 controller 时才关闭 loading，
        // 避免切换 tab 时旧请求被取消后把 loading 置 false，导致闪现暂无数据。
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [filters, setLoading, setError, setTabData, resetBlockOffsets, cancelLoad, formatLoadError],
  );

  /** 按块加载下一批明细（服务端 offset 分页），只影响触发的那张表。触发时机由 DrillTable 决定。 */
  const loadMore = useCallback(
    async (tabId: string, block: string) => {
      const key = `${tabId}|${block}`;
      if (loadingMoreRef.current[key]) return;
      const offset = blockOffsetsRef.current[key] ?? DEFAULT_LIMIT;
      if (offset < 0) return;
      const controller = new AbortController();
      loadingMoreRef.current[key] = true;
      try {
        const res = await dashboardService.fetchBlock(
          tabId,
          block,
          filters,
          { offset, limit: DEFAULT_LIMIT, ...blockSortsRef.current[key] },
          false,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          if (res.rows.length > 0) {
            appendBlockRows(tabId, block, res.rows, res.total);
            blockOffsetsRef.current[key] = offset + DEFAULT_LIMIT;
          } else {
            // 该块已全部加载完，标记 -1 不再请求
            blockOffsetsRef.current[key] = -1;
          }
        }
      } catch (err) {
        console.error('Failed to load more rows', err);
        const message = formatLoadError(err);
        if (message) setError(message);
      } finally {
        loadingMoreRef.current[key] = false;
      }
    },
    [filters, appendBlockRows, setError, formatLoadError],
  );

  /** 下钻时按需加载目标块数据：用当前全局筛选 + 下钻维度过滤，从第 0 行开始取 */
  const drillBlock = useCallback(
    async (tabId: string, block: string, drillFilters: DashboardFilters) => {
      const key = `${tabId}|${block}`;
      if (loadingMoreRef.current[key]) return null;
      const controller = new AbortController();
      loadingMoreRef.current[key] = true;
      try {
        const res = await dashboardService.fetchBlock(
          tabId,
          block,
          { ...filters, ...drillFilters },
          { offset: 0, limit: DEFAULT_LIMIT },
          true,
          controller.signal,
        );
        return res;
      } catch (err) {
        console.error('Failed to drill block', err);
        const message = formatLoadError(err);
        if (message) setError(message);
        return null;
      } finally {
        loadingMoreRef.current[key] = false;
      }
    },
    [filters, setError, formatLoadError],
  );

  /** 按块重新排序加载（从第 0 行开始，替换该块数据） */
  const sortBlock = useCallback(
    async (tabId: string, block: string, sortCol: string, sortOrder: 'asc' | 'desc') => {
      const key = `${tabId}|${block}`;
      if (loadingMoreRef.current[key]) return;
      const controller = new AbortController();
      loadingMoreRef.current[key] = true;
      try {
        const res = await dashboardService.fetchBlock(
          tabId,
          block,
          filters,
          { offset: 0, limit: DEFAULT_LIMIT, sortCol, sortOrder },
          false,
          controller.signal,
        );
        if (!controller.signal.aborted) {
          replaceBlockRows(tabId, block, res.rows, res.total);
          blockSortsRef.current[key] = { sortCol, sortOrder };
          blockOffsetsRef.current[key] = res.rows.length < DEFAULT_LIMIT ? -1 : DEFAULT_LIMIT;
        }
      } catch (err) {
        console.error('Failed to sort block', err);
        const message = formatLoadError(err);
        if (message) setError(message);
      } finally {
        loadingMoreRef.current[key] = false;
      }
    },
    [filters, replaceBlockRows, setError, formatLoadError],
  );

  /** 筛选变化后静默刷新数据（不触发全局 loading，避免 KPI/图表闪烁） */
  const reloadTab = useCallback(
    async (tabId: string, activeFilters: DashboardFilters) => {
      cancelLoad();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setError(null);
      try {
        const data = await dashboardService.fetchTab(tabId, activeFilters, undefined, controller.signal);
        if (!controller.signal.aborted) {
          setTabData(tabId, data);
          resetBlockOffsets(tabId);
        }
      } catch (err) {
        const message = formatLoadError(err);
        if (message) setError(message);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [setError, setTabData, resetBlockOffsets, cancelLoad, formatLoadError],
  );

  const loadMeta = useCallback(async () => {
    try {
      const data = await dashboardService.fetchDashboard();
      setMeta(data.global_period, data.filter_info);
    } catch (err) {
      // 元数据加载失败不阻断页面
      console.error('Failed to load dashboard meta', err);
    }
  }, [setMeta]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    // switchTab 已显式加载过该 tab，跳过避免重复请求
    if (pendingSwitchTabRef.current === currentTab) {
      pendingSwitchTabRef.current = null;
      return;
    }
    if (!tabData[currentTab]) {
      loadTab(currentTab);
    }
  }, [currentTab, tabData, loadTab]);

  /** 切换 tab：保留当前全局筛选并重新加载，新 tab 自动应用已有筛选 */
  const switchTab = useCallback(
    async (tabId: string) => {
      cancelLoad();
      pendingSwitchTabRef.current = tabId;
      setCurrentTab(tabId);
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const data = await dashboardService.fetchTab(tabId, filters, undefined, controller.signal);
        if (!controller.signal.aborted) {
          setTabData(tabId, data);
          resetBlockOffsets(tabId);
        }
      } catch (err) {
        const message = formatLoadError(err);
        if (message) setError(message);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [filters, setCurrentTab, setLoading, setError, setTabData, resetBlockOffsets, cancelLoad, formatLoadError],
  );

  const applyFilters = useCallback(
    async (newFilters: DashboardFilters) => {
      setFilters(newFilters);
      await reloadTab(currentTab, newFilters);
    },
    [currentTab, setFilters, reloadTab],
  );

  /** 一键切换 tab 并应用筛选，可选滚动到页面内 block（用于智能助手导航） */
  const navigateToTab = useCallback(
    async (tabId?: string, newFilters?: DashboardFilters, blockKey?: string) => {
      if (!tabId) return;
      cancelLoad();
      pendingSwitchTabRef.current = tabId;
      setCurrentTab(tabId);
      const finalFilters = newFilters || {};
      setFilters(finalFilters);
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const data = await dashboardService.fetchTab(tabId, finalFilters, undefined, controller.signal);
        if (!controller.signal.aborted) {
          setTabData(tabId, data);
          resetBlockOffsets(tabId);

          if (blockKey) {
            // 等待 React 渲染 + 数据加载完成后再滚动
            setTimeout(() => {
              const el = document.querySelector(`[data-block-key="${blockKey}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // 高亮一下，让用户注意到
                el.classList.add('block-highlight');
                setTimeout(() => el.classList.remove('block-highlight'), 2000);
              }
            }, 300);
          }
        }
      } catch (err) {
        const message = formatLoadError(err);
        if (message) setError(message);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          setLoading(false);
        }
      }
    },
    [
      setCurrentTab,
      setFilters,
      setLoading,
      setError,
      setTabData,
      resetBlockOffsets,
      cancelLoad,
      formatLoadError,
    ],
  );

  /** 统一处理助手返回的 action：导航 / 设筛选 / 下钻建议 / 问题推荐 */
  const applyChatAction = useCallback(
    async (action: ChatAction) => {
      if (action.type === 'navigate') {
        await navigateToTab(action.tabId, action.filters, action.blockKey);
      } else if (action.type === 'set_filter') {
        const next = action.replace ? action.filters : { ...filters, ...action.filters };
        await applyFilters(next);
      } else if (action.type === 'drill_down') {
        const tabId = action.tabId || currentTab;
        const nextFilters = { ...filters, [action.dim]: action.value || '' };
        await navigateToTab(tabId, nextFilters, action.blockKey);
      }
      // show_suggestions 不需要执行动作，前端展示即可
    },
    [navigateToTab, applyFilters, filters, currentTab],
  );

  return {
    currentTab,
    filters,
    tabData,
    loading,
    error,
    globalPeriod,
    filterInfo,
    switchTab,
    applyFilters,
    navigateToTab,
    applyChatAction,
    refresh: () => loadTab(currentTab),
    loadMore,
    sortBlock,
    drillBlock,
    openModal,
    closeModal,
  };
}
